import React, { FC, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Page, Text, View, Document, StyleSheet, pdf, Line, Svg, Image } from '@react-pdf/renderer';

import { FundTypeEnum, GoldTypeEnum, SilverTypeEnum, TransactionActionEnum, TransactionStatusEnum } from '~utils/enums';

import AffinLogo from '~assets/images/affin-logo-v1.png';
import ActionButton from '~components/atoms/ActionButton';
import { TCustomerDetails, TGetCollateralListing } from '~types/feat/customer';
import { useAppSelector } from '~redux/hooks';
import { TBranchFilter } from '~types/feat/branch';

type PageProps = {
    children: React.ReactNode;
    title: string;
    subtitle: string;
    copy: string;
    isCustomerCopy?: boolean;
}

type ReceiptPDFProps = {
    collateralDetails: TGetCollateralListing;
    customerDetails: TCustomerDetails;
    collateralBranchDetails?: TBranchFilter;
}

const ReceiptPDF: FC<ReceiptPDFProps> = ({
    collateralDetails,
    customerDetails,
    collateralBranchDetails,
}) => {
    const inventoryList = useAppSelector((state) => state.gold.allInventories);
    const branchCode = useAppSelector(state => state.auth.userDetails.branchCode);
    const branches = useAppSelector(state => state.branch.allBranches);

    const userBranch = branches.find(branch => branch.branchCode === branchCode);

    const userBranchName = userBranch?.branchName ?? '-';
    const userBranchCode = userBranch?.branchCode ?? '-';

    const [loading, setLoading] = useState(false);
    const [newSummaryTable, setNewSummaryTable] = useState<Array<{ label: string; value: string | number; }>>([]);

    const getRegisterCategory = () => {
        const product = inventoryList.find((inventory) => inventory.productId === collateralDetails.productId);

        if (!product) return '-';

            return `${FundTypeEnum[product.fundType]} | ${GoldTypeEnum[product.goldType]?.replace('Gold', ' Gold') || SilverTypeEnum[product.silverType]?.replace('Silver', ' Silver')}`;
    };

    const summaryTable = [
        {
            label: 'Collateral Status',
            value: TransactionStatusEnum[collateralDetails.status],
        },
        {
            label: 'Collateral Date',
            value: dayjs(new Date()).format('DD/MM/YYYY hh:mm A'),
        },
        {
            label: 'Collateral End Date',
            value: collateralDetails.collateralEndDate ? dayjs(collateralDetails.collateralEndDate).format('DD/MM/YYYY hh:mm A') : '-',
        },
        // {
        //     label: 'Project Name',
        //     value: buyOrSellGoldParams.projectName ? buyOrSellGoldParams.projectName : '-',
        // },
        {
            label: 'Region Name',
            value: collateralBranchDetails?.regionName ?? '-',
        },
        {
            label: 'Branch Name',
            value: collateralBranchDetails?.branchName ?? '-',
        },
        {
            label: 'Branch Code',
            value: collateralBranchDetails?.branchCode ?? '-',
        },
        {
            label: 'Register Category',
            value: getRegisterCategory(),
        },
        {
            label: 'Name of Customer/Company Firm/Association',
            value: customerDetails.name,
        },
        {
            label: 'Identification No.',
            value: customerDetails.idNo,
        },
        {
            label: 'CIF No.',
            value: customerDetails.CIFNo,
        },
        {
            label: 'Price per gram (MYR)',
            value: collateralDetails.paramGoldPrice === null ? `RM${(Math.round(Number(collateralDetails.baseAPIPrice) * 100) / 100).toFixed(2)}` : `RM${(Math.round(Number(collateralDetails.paramGoldPrice) * 100) / 100).toFixed(2)}`,
        },
        {
            label: 'Quantity',
            value: collateralDetails.amount,
        },
        {
            label: 'Total Amount (MYR)',
            value: `RM${(Math.round(Number(collateralDetails.marketValue) * 100) / 100).toFixed(2)}`,
        },
    ];

    useEffect(() => {
        if (customerDetails.additionalUserDetails) {
            setNewSummaryTable(
                [
                    {
                        label: 'Collateral Status',
                        value: TransactionStatusEnum[collateralDetails.status],
                    },
                    {
                        label: 'Collateral Date',
                        value: dayjs(new Date()).format('DD/MM/YYYY hh:mm A'),
                    },
                    {
                        label: 'Collateral End Date',
                        value: collateralDetails.collateralEndDate ? dayjs(collateralDetails.collateralEndDate).format('DD/MM/YYYY hh:mm A') : '-',
                    },
                    // {
                    //     label: 'Project Name',
                    //     value: buyOrSellGoldParams.projectName ? buyOrSellGoldParams.projectName : '-',
                    // },
                    {
                        label: 'Region Name',
                        value: collateralBranchDetails?.regionName ?? '-',
                    },
                    {
                        label: 'Branch Name',
                        value: collateralBranchDetails?.branchName ?? '-',
                    },
                    {
                        label: 'Branch Code',
                        value: collateralBranchDetails?.branchCode ?? '-',
                    },
                    {
                        label: 'Register Category',
                        value: getRegisterCategory(),
                    },
                    {
                        label: 'Name of Customer/Company Firm/Association',
                        value: customerDetails.name,
                    },
                    {
                        label: 'Identification No.',
                        value: customerDetails.idNo,
                    },
                    {
                        label: 'CIF No.',
                        value: customerDetails.CIFNo,
                    },
                    {
                        label: 'Price per gram (MYR)',
                        value: collateralDetails.paramGoldPrice === null ? `RM${(Math.round(Number(collateralDetails.baseAPIPrice) * 100) / 100).toFixed(2)}` : `RM${(Math.round(Number(collateralDetails.paramGoldPrice) * 100) / 100).toFixed(2)}`,
                    },
                    {
                        label: 'Quantity',
                        value: collateralDetails.amount,
                    },
                    {
                        label: 'Total Amount (MYR)',
                        value: `RM${(Math.round(Number(collateralDetails.marketValue) * 100) / 100).toFixed(2)}`,
                    },
                ]
            );
        }
        else {
            setNewSummaryTable(summaryTable);
        }
    }, [customerDetails]);

    const goldTable = {
        productName: collateralDetails.productName,
        pricePerGram: collateralDetails.paramGoldPrice === null ? `RM${(Math.round(Number(collateralDetails.baseAPIPrice) * 100) / 100).toFixed(2)}` : `RM${(Math.round(Number(collateralDetails.paramGoldPrice) * 100) / 100).toFixed(2)}`,
    };

    const PageWithHeaderFooter = ({
        children,
        title,
        subtitle,
        copy,
        isCustomerCopy,
    }: PageProps) => (
        <Page size='A4' style={isCustomerCopy ? styles.customerPage : styles.page} orientation='landscape'>
            {/* Header */}
            <View style={styles.header}>
                {
                    isCustomerCopy &&
                    <Image
                        style={styles.headerLogo}
                        src={AffinLogo}
                    />
                }
                <View style={styles.headerContent}>
                    <Text style={styles.headerTitle}>{title}</Text>
                    <Text style={styles.headerSubtitle}>{subtitle}</Text>
                </View>
                <Text style={styles.headerCopy}>{copy}</Text>
                <View style={styles.divider} />
            </View>

            {/* Main Content */}
            <View style={styles.mainContent}>
                {children}
            </View>
        </Page>
    );

    const renderDetails = () => {
        return (
            <View style={styles.detailsTable}>
                {newSummaryTable.map((item, index) => (
                    <View style={styles.detailsRow} key={index}>
                        <Text style={styles.detailsCell}>{item.label}</Text>
                        <Text style={styles.detailsCell}>{`: ${item.value}`}</Text>
                    </View>
                ))}
            </View>
        );
    };

    const renderGoldDescription = () => {
        return (
            <View>
                <Text style={styles.goldDescriptionTitle}>Description / Deskripsi</Text>
                <View style={styles.goldTable}>
                    <View style={styles.goldTableRow}>
                        <Text style={styles.goldHeaderCell}>Product / Produk</Text>
                        <Text style={styles.goldHeaderCellPrice}>Price Per Gram / Harga Segram</Text>
                    </View>
                    <View style={styles.goldTableRow}>
                        <Text style={[styles.goldTableCell, { width: '70%' }]}>{goldTable.productName}</Text>
                        <Text style={[styles.goldTableCell, { width: '30%' }]}>{goldTable.pricePerGram}</Text>
                    </View>
                </View>
            </View>
        );
    };

    const renderDeclaration = () => {
        return (
            <View>
                <View style={styles.declarationContainer}>
                    <Text style={styles.declarationText}>Note: This receipt is computer generated and no signature is required.</Text>
                    <Text style={styles.declarationText}>Nota: Resit ini adalah cetakan komputer dan tandatangan tidak diperlukan.</Text>
                </View>
            </View>
        );
    };

    const handleOpenPDFInNewTab = () => {
        setLoading(true);

        const docTitle = 'COLLATERAL/CAGARAN';

        // Create the PDF document
        const doc = (
            <Document>
                <PageWithHeaderFooter
                    title={docTitle}
                    subtitle='RECEIPT / RESIT'
                    copy={'Customer\'s Copy'}
                    isCustomerCopy
                >
                    {renderDetails()}
                    {renderGoldDescription()}
                    {renderDeclaration()}
                </PageWithHeaderFooter>

                <PageWithHeaderFooter
                    title={docTitle}
                    subtitle='RECEIPT / RESIT'
                    copy={'Bank\'s Copy'}
                >
                    {renderDetails()}
                    {renderGoldDescription()}
                    {renderDeclaration()}
                </PageWithHeaderFooter>
            </Document>
        );

        // Generate PDF Blob and open in new tab
        pdf(doc).toBlob().then(() => {
            const promise = pdf(doc).toBlob();
            promise.then((blob) => {
                const pdfUrl = URL.createObjectURL(blob);
                setLoading(false);
                window.open(pdfUrl, '_blank');
            }).catch(() => {
                setLoading(false);
            });
        });
    };
    return (
        <div>
            <ActionButton
                label='Print Receipt'
                onClick={handleOpenPDFInNewTab}
                isLoading={loading}
            />
        </div>
    );
};

// Create styles
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        paddingTop: 50, // Space for header
        // paddingBottom: 80, // Space for footer
        paddingHorizontal: 20,
        position: 'relative',
    },
    customerPage: {
        flexDirection: 'column',
        paddingTop: 100, // Space for header
        // paddingBottom: 80, // Space for footer
        paddingHorizontal: 20,
        position: 'relative',
    },
    header: {
        position: 'absolute',
        top: 10,
        left: 20,
        right: 20,
        display: 'flex',
        flexDirection: 'column',
    },
    headerLogo: {
        width: 162,
        height: 50,
        padding: 5,
    },
    headerContent: {
        flexDirection: 'column',
        gap: 3,
        marginBottom: 5,
    },
    headerTitle: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
    },
    headerSubtitle: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
    },
    headerCopy: {
        position: 'absolute',
        top: 5,
        right: 20,
        fontSize: 6,
        color: '#666',
    },
    divider: {
        height: 2,
        backgroundColor: '#000',
        marginVertical: 3,
    },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 20,
        right: 20,
        display: 'flex',
        flexDirection: 'column',
    },
    footerContent: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 10,
    },
    pageNumber: {
        fontSize: 6,
        color: '#666',
    },
    mainContent: {
        flex: 1,
    },
    detailsTable: {
        display: 'flex',
        width: '100%',
        fontSize: 6,
        gap: 2,
        marginBottom: 15,
    },
    detailsRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    detailsCell: {
        width: '50%',
    },
    goldDescriptionTitle: {
        marginBottom: 5,
        fontSize: 6,
        fontFamily: 'Helvetica-Bold',
        textDecoration: 'underline',
    },
    goldTable: {
        width: '60%', // Full width
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#000000',
        borderBottomWidth: 0,
        borderRightWidth: 0,
        fontSize: 6,
        marginBottom: 15,
    },
    goldTableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#000000',
    },
    goldHeaderCell: {
        width: '70%', // First column
        padding: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
        fontWeight: 'bold',
    },
    goldHeaderCellPrice: {
        width: '30%', // Second column
        padding: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
        fontWeight: 'bold',
    },
    goldTableCell: {
        padding: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
    },
    declarationContainer: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: 6,
    },
    declarationTitle: {
        marginBottom: 5,
        fontSize: 6,
        fontFamily: 'Helvetica-Bold',
        textDecoration: 'underline',
    },
    declarationText: {
        fontSize: 6,
    },
    declarationTextBold: {
        fontSize: 6,
        fontFamily: 'Helvetica-Bold',
    },
    signatureContainer: {
        display: 'flex',
        width: '100%',
        fontSize: 6,
        gap: 1,
        marginBottom: 20,
    },
    signatureRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    signatureCell: {
        width: '50%',
    },
    extraSignatureContainer: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        fontSize: 6,
        gap: 5,
        marginBottom: 15,
    },
    extraSignature: {
        width: '50%',
    },
    extraSignatureRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    extraSignatureCell: {
        width: '40%',
        maxLines: 1,
    },
    bankTable: {
        width: '100%', // Full width
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#000000',
        borderBottomWidth: 0,
        borderRightWidth: 0,
        fontSize: 6,
        marginBottom: 15,
    },
    bankTableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#000000',
    },
    bankHeaderCell: {
        width: '100%', // First column
        padding: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
        fontWeight: 'bold',
    },
    bankTableCell: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: 4,
        borderRightWidth: 1,
        borderColor: '#000000',
    },
    receiptFooterContainer: {
        flexDirection: 'column',
        marginBottom: 5,
        marginTop: 20,
    },
    receiptFooterTitle: {
        fontSize: 6,
    },
    receiptFooterSubtitle: {
        fontSize: 6,
    },
});

export default ReceiptPDF;
